import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Formulieren = () => {
  const { gebruiker } = useAuth();
  const navigate = useNavigate();
  const ADMIN_ROLLEN = ['hoofdbeheerder','super_admin','bedrijf_admin','werkvoorbereider','manager','hr'];
  const isBeheerder = ADMIN_ROLLEN.includes(gebruiker?.rol);

  const [tab, setTab] = useState('beschikbaar');
  const [templates, setTemplates] = useState([]);
  const [invullingen, setInvullingen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actiefTemplate, setActiefTemplate] = useState(null);

  useEffect(() => { laadAlles(); }, [tab]);

  const laadAlles = async () => {
    setLoading(true);
    try {
      const promises = [api.get('/formulier-templates')];
      promises.push(api.get('/formulier-invullingen'));
      const [tR, iR] = await Promise.all(promises);
      setTemplates(tR.data.data || []);
      setInvullingen(iR.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 20px' }}>
        <h1 style={{ margin: 0, fontSize: 24, color: '#1e293b' }}>📋 Formulieren</h1>
        {isBeheerder && (
          <button onClick={() => navigate('/forms')}
            style={{ padding: '10px 18px', background: '#00D4AA', color: '#0a0e1a', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
            + Nieuw formulier
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: 20 }}>
        <button onClick={() => setTab('beschikbaar')}
          style={tabStyle(tab === 'beschikbaar')}>📝 Beschikbare formulieren</button>
        <button onClick={() => setTab('ingediend')}
          style={tabStyle(tab === 'ingediend')}>📤 Mijn ingezonden ({invullingen.filter(i => i.gebruiker_id === gebruiker?.id).length})</button>
        {isBeheerder && (
          <button onClick={() => setTab('te-behandelen')}
            style={tabStyle(tab === 'te-behandelen')}>
            ⏳ Te behandelen ({invullingen.filter(i => i.status === 'ingediend').length})
          </button>
        )}
      </div>

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Laden...</div> :
        tab === 'beschikbaar' ? (
          templates.length === 0 ? (
            <div style={emptyStyle}>Geen formulieren beschikbaar.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {templates.map(t => (
                <div key={t.id}
                  onClick={() => setActiefTemplate(t)}
                  style={{
                    background: 'white', border: '1px solid #e5e7eb', borderRadius: 12,
                    padding: 20, cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37,99,235,0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ fontSize: 24, marginBottom: 8 }}>📋</div>
                  <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#1e293b' }}>{t.naam}</h3>
                  {t.beschrijving && <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>{t.beschrijving}</p>}
                  <div style={{ marginTop: 10, fontSize: 11, color: '#94a3b8' }}>
                    {Array.isArray(t.velden) ? t.velden.length : (t.velden?.length || 0)} velden
                    {t.vereist_goedkeuring && ' · vereist goedkeuring'}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : tab === 'ingediend' ? (
          <InvullingenLijst
            items={invullingen.filter(i => i.gebruiker_id === gebruiker?.id || i.voor_gebruiker_id === gebruiker?.id)}
            isBeheerder={false}
            onUpdate={laadAlles}
            gebruiker={gebruiker}
          />
        ) : (
          <InvullingenLijst
            items={invullingen.filter(i => i.status === 'ingediend')}
            isBeheerder={true}
            onUpdate={laadAlles}
            gebruiker={gebruiker}
          />
        )
      }

      {actiefTemplate && (
        <FormulierModal
          template={actiefTemplate}
          onClose={() => setActiefTemplate(null)}
          onSuccess={() => { setActiefTemplate(null); laadAlles(); setTab('ingediend'); }}
          gebruiker={gebruiker}
          isBeheerder={isBeheerder}
        />
      )}
    </div>
  );
};

const tabStyle = (active) => ({
  padding: '12px 20px', border: 'none',
  borderBottom: active ? '3px solid #2563eb' : '3px solid transparent',
  background: 'none', color: active ? '#2563eb' : '#6b7280',
  fontWeight: active ? 600 : 400, cursor: 'pointer', fontSize: 14
});

const emptyStyle = { padding: 60, textAlign: 'center', color: '#94a3b8', background: '#f9fafb', borderRadius: 12 };

// ============================================================
// Form modal — render velden uit JSON
// ============================================================
const FormulierModal = ({ template, onClose, onSuccess, gebruiker, isBeheerder }) => {
  const [data, setData] = useState({});
  const [voorGebruikerId, setVoorGebruikerId] = useState('');
  const [monteurs, setMonteurs] = useState([]);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState('');

  useEffect(() => {
    if (template.kan_voor_iemand_anders && isBeheerder) {
      api.get('/gebruikers').then(r => {
        const lijst = r.data.gebruikers || r.data || [];
        setMonteurs(lijst.filter(g => g.actief !== false));
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [template.id]);

  const velden = Array.isArray(template.velden) ? template.velden :
                 (typeof template.velden === 'string' ? JSON.parse(template.velden) : []);

  const submit = async () => {
    setFout('');
    for (const v of velden) {
      if (v.verplicht && (!data[v.naam] || String(data[v.naam]).trim() === '')) {
        setFout(`"${v.label || v.naam}" is verplicht`);
        return;
      }
    }
    setBezig(true);
    try {
      await api.post('/formulier-invullingen', {
        template_id: template.id,
        velden_data: data,
        voor_gebruiker_id: voorGebruikerId || null
      });
      onSuccess();
    } catch (e) {
      setFout(e.response?.data?.message || e.message);
    } finally { setBezig(false); }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 6px', fontSize: 22 }}>{template.naam}</h2>
        {template.beschrijving && <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>{template.beschrijving}</p>}

        {template.kan_voor_iemand_anders && isBeheerder && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Voor wie? *</label>
            <select value={voorGebruikerId} onChange={e => setVoorGebruikerId(e.target.value)} style={inputStyle}>
              <option value="">— Mezelf —</option>
              {monteurs.map(m => <option key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</option>)}
            </select>
          </div>
        )}

        {velden.map(v => (
          <div key={v.naam} style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
              {v.label || v.naam}{v.verplicht && ' *'}
            </label>
            {v.type === 'textarea' ? (
              <textarea value={data[v.naam] || ''} onChange={e => setData({...data, [v.naam]: e.target.value})}
                rows={3} style={inputStyle} />
            ) : v.type === 'select' ? (
              <select value={data[v.naam] || ''} onChange={e => setData({...data, [v.naam]: e.target.value})}
                style={inputStyle}>
                <option value="">— Kies —</option>
                {(v.opties || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : v.type === 'number' ? (
              <input type="number" value={data[v.naam] || ''}
                onChange={e => setData({...data, [v.naam]: e.target.value})} style={inputStyle} />
            ) : v.type === 'date' ? (
              <input type="date" value={data[v.naam] || ''}
                onChange={e => setData({...data, [v.naam]: e.target.value})} style={inputStyle} />
            ) : (
              <input type="text" value={data[v.naam] || ''}
                onChange={e => setData({...data, [v.naam]: e.target.value})} style={inputStyle} />
            )}
          </div>
        ))}

        {template.vereist_goedkeuring && (
          <div style={{ padding: '10px 14px', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, fontSize: 13, color: '#1e40af', marginBottom: 14 }}>
            ℹ️ Deze aanvraag moet door HR/werkvoorbereider goedgekeurd worden.
          </div>
        )}

        {fout && <div style={errorStyle}>❌ {fout}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={bezig} style={btnSecondary}>Annuleren</button>
          <button onClick={submit} disabled={bezig} style={{...btnPrimary, opacity: bezig ? 0.6 : 1}}>
            {bezig ? 'Versturen...' : '📤 Versturen'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Lijst van invullingen
// ============================================================
const InvullingenLijst = ({ items, isBeheerder, onUpdate, gebruiker }) => {
  const handleAction = async (item, actie) => {
    let opmerking = null;
    if (actie === 'afwijzen') {
      opmerking = window.prompt('Reden van afwijzing (optioneel):') || '';
    }
    try {
      await api.put(`/formulier-invullingen/${item.id}/${actie}`, { opmerking });
      onUpdate();
    } catch (e) {
      alert('Fout: ' + (e.response?.data?.message || e.message));
    }
  };

  const downloadPdf = (id) => {
    const token = localStorage.getItem('token');
    const url = (api.defaults.baseURL || '') + '/formulier-invullingen/' + id + '/pdf';
    fetch(url, { headers: { 'Authorization': 'Bearer ' + token } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'formulier-' + id + '.pdf';
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(e => alert('Download mislukt: ' + e.message));
  };

  if (items.length === 0) {
    return <div style={emptyStyle}>Geen items.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map(i => {
        const statusKleur = i.status === 'goedgekeurd' ? '#10b981' :
                            i.status === 'afgewezen' ? '#ef4444' : '#f59e0b';
        return (
          <div key={i.id} style={{
            background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
            padding: 16, display: 'flex', alignItems: 'center', gap: 16
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 15, color: '#1e293b' }}>
                {i.template_naam}
                <span style={{
                  marginLeft: 10, padding: '2px 10px', borderRadius: 12,
                  background: statusKleur + '22', color: statusKleur,
                  fontSize: 11, fontWeight: 600
                }}>{i.status}</span>
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                {isBeheerder && i.gebruiker_naam && <>👤 {i.gebruiker_naam} · </>}
                📅 {new Date(i.ingediend_op).toLocaleString('nl-NL')}
                {i.behandeld_door_naam && <> · behandeld door {i.behandeld_door_naam}</>}
              </div>
              {i.behandeld_opmerking && (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, fontStyle: 'italic' }}>
                  "{i.behandeld_opmerking}"
                </div>
              )}
            </div>
            {i.pdf_pad && (
              <button onClick={() => downloadPdf(i.id)} style={btnSmall}>⬇️ PDF</button>
            )}
            {isBeheerder && i.status === 'ingediend' && (
              <>
                <button onClick={() => handleAction(i, 'goedkeuren')}
                  style={{...btnSmall, background: '#10b981', color: 'white', border: 'none'}}>✓ Goedkeuren</button>
                <button onClick={() => handleAction(i, 'afwijzen')}
                  style={{...btnSmall, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca'}}>✗ Afwijzen</button>
              </>
            )}
            {i.status === 'goedgekeurd' && i.vereist_sap_bevestiging && !i.sap_aangevraagd_op && (i.gebruiker_id === gebruiker?.id || i.voor_gebruiker_id === gebruiker?.id) && (
              <button onClick={async () => {
                try {
                  await api.put('/formulier-invullingen/' + i.id + '/sap-bevestigen', {});
                  onUpdate();
                } catch (e) { alert('Fout: ' + (e.response?.data?.message || e.message)); }
              }} style={{...btnSmall, background: '#2563eb', color: 'white', border: 'none'}}>
                ✓ Aangevraagd in SAP
              </button>
            )}
            {i.sap_aangevraagd_op && (
              <span style={{fontSize: 11, color: '#10b981', fontWeight: 600}}>✓ SAP bevestigd</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const modalBox = { background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto' };
const errorStyle = { padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#dc2626', marginBottom: 14 };
const btnPrimary = { padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { padding: '10px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer' };
const btnSmall = { padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', background: 'white', border: '1px solid #d1d5db' };

export default Formulieren;
